import * as React from 'react'

import * as ReactDOM from 'react-dom'

import IFilePreviewProperties from '~/client/src/shared/interfaces/IFilePreviewProperties'

import FileFullscreenPreview from '../components/FileFullscreenPreview/FileFullscreenPreview'

interface FilePreviewContextValue {
  showPreview: (files: IFilePreviewProperties[], index: number) => void
}

const FilePreviewContext = React.createContext<FilePreviewContextValue>({
  showPreview: () => null,
})

export function useFilePreview(): FilePreviewContextValue {
  return React.useContext(FilePreviewContext)
}

function stopPropagation(e: React.SyntheticEvent) {
  e.stopPropagation()
}

function useBlockBackgroundEvents(isActive: boolean) {
  React.useEffect(() => {
    if (!isActive) return
    document.body.style.pointerEvents = 'none'
    return () => {
      document.body.style.pointerEvents = ''
    }
  }, [isActive])
}

export function FilePreviewProvider({
  children,
  isMobile,
}: {
  children: React.ReactNode
  // Set at the mobile route mount so previews opted in here take the mobile
  // path (PDF handoff / mobile layout) instead of desktop pdf.js.
  isMobile?: boolean
}) {
  const [files, setFiles] = React.useState<IFilePreviewProperties[]>([])
  const [fileIndex, setFileIndex] = React.useState(0)
  const [isVisible, setIsVisible] = React.useState(false)

  const showPreview = React.useCallback(
    (newFiles: IFilePreviewProperties[], index: number) => {
      setFiles(newFiles)
      setFileIndex(index)
      setIsVisible(true)
    },
    [],
  )

  const hidePreview = React.useCallback(() => {
    requestAnimationFrame(() => setIsVisible(false))
  }, [])

  useBlockBackgroundEvents(isVisible && files.length > 0)

  const value = React.useMemo(() => ({ showPreview }), [showPreview])

  return (
    <FilePreviewContext.Provider value={value}>
      {children}
      {isVisible &&
        files.length > 0 &&
        ReactDOM.createPortal(
          <div
            style={{ pointerEvents: 'auto' }}
            onMouseDown={stopPropagation}
            onPointerDown={stopPropagation}
            onTouchStart={stopPropagation}
          >
            <FileFullscreenPreview
              files={files}
              fileIndex={fileIndex}
              onClose={hidePreview}
              setFileIndex={setFileIndex}
              isMobile={isMobile}
              className="tw:inset-0"
            />
          </div>,
          document.body,
        )}
    </FilePreviewContext.Provider>
  )
}
